/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import type { ComponentAttrs } from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import { components } from '@glowingblue-orell-thalia-common';
import type { SliderItemAttrs } from '@glowingblue-orell-thalia-common/components/AbstractSlider';
import app from 'flarum/forum/app';
import ReviewItem from './ReviewItem';
import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';

const { AbstractSlider } = components;

export interface ReviewsCoverflowAttrs extends ComponentAttrs {
	discussions: Discussion[];
}

/**
 * At module scope so the type stays stable: a class expression returned from `sliderItemComponent`
 * would hand Mithril a new type every draw, rebuilding every slide instead of patching it.
 */
class ReviewsCoverflowItem extends Component<SliderItemAttrs<Discussion>> {
	view(): Mithril.Children {
		const { item: discussion, className } = this.attrs;
		const routeToReview = app.route.discussion(discussion);

		return (
			<Link key={discussion.id()} data-id={discussion.id()} className={`ReviewsCoverflow-slide ${className}`} href={routeToReview}>
				<ReviewItem discussion={discussion} expandable={false} linkAll={true} />
			</Link>
		);
	}
}

// TODO: add a loading skeleton — `AbstractSlider.view()` renders an empty `.Slider` wrapper while
// `items` is empty, and `loading` is available to branch on.
export default class ReviewsCoverflow extends AbstractSlider<Discussion, ReviewsCoverflowAttrs> {
	get sliderItemComponent() {
		return ReviewsCoverflowItem;
	}

	/** `AbstractSlider` re-runs this before `view()`, so slides never go stale. */
	protected getItems(attrs: ReviewsCoverflowAttrs): Discussion[] {
		return attrs.discussions;
	}

	getSwiperOptions() {
		return {
			...super.getSwiperOptions(),
			effect: 'coverflow',
			coverflowEffect: {
				rotate: 50,
				stretch: -24,
				depth: 200,
				modifier: 1,
				slideShadows: true,
			},
		};
	}

	getClassName(): string {
		return `${super.getClassName()} ReviewsCoverflow`;
	}
}
